import { useEffect, useMemo, useState } from "react";
import PaginationControls from "../../../components/base/PaginationControls";
import Select from "react-select";
import Swal from "sweetalert2";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import { useQueryClient } from "@tanstack/react-query";
import { useGroupsStore, type Group } from "../../../store/admin/groupsStore";
import { useTruckYardStore } from "../../../store/truckYardStore";
import { useAllGroups } from "../../../features/catalogs/queries";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import HeaderComponent from "../components/HeaderComponent";
import {
  NO_YARD_FILTER,
  matchesYardSelection,
} from "../components/yardMultiFilter";
import { WATER_TYPES } from "../components/waterTypes";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

const PAGE_SIZE = 12;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function groupDevicesCount(g: Group): number {
  return g.devicesCount ?? g.devices_ids?.length ?? 0;
}

function statusName(g: Group): string {
  return g.status?.name || "";
}

function isActive(g: Group): boolean {
  const s = statusName(g).toLowerCase();
  return s === "active" || s === "activo";
}

// ─── Type Badges ─────────────────────────────────────────────────────────────

function TypeBadges({
  types,
  isDarkMode,
  themeClasses,
}: {
  types: string[] | undefined;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
}) {
  if (!types || types.length === 0) {
    return (
      <span className={`text-xs italic ${themeClasses.textSecondary}`}>
        Sin tipo asignado
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((typeValue) => {
        const t = WATER_TYPES.find((x) => x.value === typeValue);
        return (
          <span
            key={typeValue}
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${
              isDarkMode
                ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                : "bg-blue-50 text-blue-700 border border-blue-200"
            }`}
          >
            {t?.label ?? typeValue}
          </span>
        );
      })}
    </div>
  );
}

// ─── Grid Card ───────────────────────────────────────────────────────────────

function GroupGridCard({
  group,
  themeClasses,
  isDarkMode,
  onAssign,
}: {
  group: Group;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onAssign: (g: Group) => void;
}) {
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-2xl p-5 hover:shadow-lg transition-all duration-200 flex flex-col gap-3`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? "bg-teal-500/20" : "bg-teal-50"
            }`}
          >
            <i className="ri-folder-user-line text-teal-500 text-lg"></i>
          </div>
          <div className="min-w-0">
            <h3
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {group.name}
            </h3>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>
              {group.id}
            </span>
          </div>
        </div>
        <span
          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${
            isActive(group) ? "bg-green-500" : "bg-gray-400"
          }`}
        />
      </div>

      <div className={`${surface} rounded-xl p-3 space-y-2`}>
        <div className="flex items-center gap-2">
          <i
            className={`ri-truck-line text-xs ${themeClasses.textSecondary}`}
          ></i>
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {groupDevicesCount(group)} vehículos
          </span>
        </div>
        {group.description && (
          <div className="flex items-start gap-2">
            <i
              className={`ri-information-line text-xs ${themeClasses.textSecondary} mt-0.5`}
            ></i>
            <span
              className={`text-xs ${themeClasses.textSecondary} line-clamp-2`}
            >
              {group.description}
            </span>
          </div>
        )}
        <div className="flex items-start gap-2">
          <i
            className={`ri-drop-line text-xs ${themeClasses.textSecondary} mt-0.5`}
          ></i>
          <TypeBadges
            types={group.type}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => onAssign(group)}
          className="flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors text-center"
          style={{ backgroundColor: brand.primary, color: brand.accent }}
        >
          <i className="ri-drop-line mr-1"></i>
          Asignar tipo
        </button>
      </div>
    </div>
  );
}

// ─── List Row ────────────────────────────────────────────────────────────────

function GroupListRow({
  group,
  themeClasses,
  isDarkMode,
  onAssign,
}: {
  group: Group;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onAssign: (g: Group) => void;
}) {
  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all duration-200`}
    >
      {/* Mobile */}
      <div className="flex flex-col gap-3 sm:hidden">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? "bg-teal-500/20" : "bg-teal-50"
            }`}
          >
            <i className="ri-folder-user-line text-teal-500 text-base"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {group.name}
            </p>
            <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
              {groupDevicesCount(group)} vehículos
            </p>
          </div>
        </div>
        <TypeBadges
          types={group.type}
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
        />
        <button
          onClick={() => onAssign(group)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium"
          style={{ backgroundColor: brand.primary, color: brand.accent }}
        >
          Asignar tipo
        </button>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? "bg-teal-500/20" : "bg-teal-50"
            }`}
          >
            <i className="ri-folder-user-line text-teal-500 text-lg"></i>
          </div>
          <div className="min-w-0">
            <p
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {group.name}
            </p>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>
              {group.id}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="hidden md:block text-center min-w-[100px]">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
            >
              Vehículos
            </p>
            <p className={`text-sm font-medium ${themeClasses.text}`}>
              {groupDevicesCount(group)}
            </p>
          </div>
          <div className="hidden lg:block min-w-[200px]">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary} mb-1`}
            >
              Tipo
            </p>
            <TypeBadges
              types={group.type}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
            />
          </div>

          <button
            onClick={() => onAssign(group)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ backgroundColor: brand.primary, color: brand.accent }}
          >
            <i className="ri-drop-line mr-1"></i>
            Asignar tipo
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Assignment Modal ────────────────────────────────────────────────────────

interface YardOption {
  value: string;
  label: string;
}

function GroupTypeAssignModal({
  themeClasses,
  isDarkMode,
  group,
  yards,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  group: Group;
  yards: { id: string; name: string; type?: string[] }[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (data: {
    types: string[];
    razonSocial: string;
    rfc: string;
    yard_ids: string[];
  }) => void;
}) {
  const [types, setTypes] = useState<string[]>(group.type || []);
  const [razonSocial, setRazonSocial] = useState<string>(
    group.razonSocial || "",
  );
  // Fallback to the legacy `nis` field so groups saved before the
  // NIS → RFC rename still show their identifier when edited.
  const [rfc, setRfc] = useState<string>(group.rfc ?? group.nis ?? "");
  const [yardIds, setYardIds] = useState<string[]>(group.yard_ids || []);

  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const cardCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} text-sm`;
  const labelCls = `text-xs font-medium ${
    isDarkMode ? "text-gray-400" : "text-gray-500"
  } mb-1.5 block`;

  // Patios offered for selection are only those whose own type list intersects
  // the types currently selected for the group. With no types selected, no
  // patio can match, so the selector is empty.
  const matchingYards = useMemo(
    () => yards.filter((y) => (y.type || []).some((t) => types.includes(t))),
    [yards, types],
  );

  const yardOptions: YardOption[] = useMemo(
    () => matchingYards.map((y) => ({ value: y.id, label: y.name })),
    [matchingYards],
  );

  // When the type selection changes, drop any chosen patios that no longer
  // match so we never submit a patio outside the allowed set.
  useEffect(() => {
    const allowed = new Set(matchingYards.map((y) => y.id));
    setYardIds((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [matchingYards]);

  const selectedYardOptions = yardOptions.filter((o) =>
    yardIds.includes(o.value),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      types,
      razonSocial: razonSocial.trim(),
      rfc: rfc.trim(),
      yard_ids: yardIds,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>
            Asignar tipo al grupo
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className={labelCls}>Grupo</label>
            <div className={`${cardCls} flex items-center gap-3 opacity-90`}>
              <i className="ri-folder-user-line text-teal-500"></i>
              <div className="min-w-0">
                <p className={`text-sm ${themeClasses.text} truncate`}>
                  {group.name}
                </p>
                <p
                  className={`text-[10px] font-mono ${themeClasses.textSecondary}`}
                >
                  {group.id}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls}>Tipo</label>
            <div className="grid grid-cols-1 gap-2">
              {WATER_TYPES.map((type) => (
                <label
                  key={type.value}
                  className={`flex items-center gap-3 cursor-pointer ${surface} rounded-xl px-3 py-2.5`}
                >
                  <input
                    type="checkbox"
                    checked={types.includes(type.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setTypes((prev) => [...prev, type.value]);
                      } else {
                        setTypes((prev) =>
                          prev.filter((t) => t !== type.value),
                        );
                      }
                    }}
                    className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                  />
                  <span className={`text-sm ${themeClasses.text}`}>
                    {type.label}
                  </span>
                </label>
              ))}
            </div>
            <p className={`text-xs ${themeClasses.textSecondary} mt-1.5`}>
              Los vehículos asignados a este grupo heredarán estos tipos.
            </p>
          </div>

          <div>
            <label className={labelCls}>Razón Social</label>
            <input
              type="text"
              value={razonSocial}
              onChange={(e) => setRazonSocial(e.target.value)}
              className={cardCls}
              placeholder="Ej. Aguas del Norte S.A. de C.V."
            />
          </div>

          <div>
            <label className={labelCls}>RFC</label>
            <input
              type="text"
              value={rfc}
              onChange={(e) => setRfc(e.target.value)}
              className={cardCls}
              placeholder="Ej. AAN010101AB1"
            />
          </div>

          <div>
            <label className={labelCls}>Patios</label>
            <Select
              isMulti
              options={yardOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={
                types.length === 0
                  ? "Seleccione un tipo primero..."
                  : "Seleccione uno o más patios..."
              }
              value={selectedYardOptions}
              onChange={(opts: any) =>
                setYardIds((opts || []).map((o: YardOption) => o.value))
              }
              isDisabled={types.length === 0}
              isSearchable
              noOptionsMessage={() =>
                types.length === 0
                  ? "Seleccione un tipo primero"
                  : "No hay patios que coincidan con los tipos seleccionados"
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <p className={`text-xs ${themeClasses.textSecondary} mt-1.5`}>
              Solo se muestran los patios cuyo tipo coincide con los tipos
              seleccionados arriba.
            </p>
          </div>

          <div
            className={`flex justify-end gap-3 pt-4 border-t ${themeClasses.cardBorder}`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`px-5 py-2.5 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-xl ${themeClasses.buttonText} font-medium text-sm transition-colors`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Guardando...
                </>
              ) : (
                "Guardar tipo"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Pagination ──────────────────────────────────────────────────────────────

function GroupsPagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <PaginationControls
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
    />
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({
  themeClasses,
  isDarkMode,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div
        className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
          isDarkMode ? "bg-dark-card" : "bg-gray-100"
        }`}
      >
        <i
          className={`ri-folder-user-line text-4xl ${themeClasses.textSecondary}`}
        ></i>
      </div>
      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>
        Sin grupos disponibles
      </h3>
      <p className={`text-sm ${themeClasses.textSecondary} mb-4`}>
        No se encontraron grupos para esta compañía.
      </p>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function GroupsSection() {
  const { isDarkMode } = useDarkMode();
  const { groups, updateGroup } = useGroupsStore();
  const groupsQuery = useAllGroups();
  const loading = groupsQuery.isLoading;
  const queryClient = useQueryClient();
  const yards = useTruckYardStore((state) => state.yards);
  const fetchYards = useTruckYardStore((state) => state.fetchYards);

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string>("");
  // Selección múltiple: [] = todos, NO_YARD_FILTER suma los grupos sin patio.
  const [selectedYards, setSelectedYards] = useState<string[]>([]);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);

  const [assignGroup, setAssignGroup] = useState<Group | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchYards();
  }, [fetchYards]);

  const filtered = useMemo(() => {
    return groups.filter((g) => {
      const matchSearch =
        !searchQuery ||
        g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (g.description || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        g.id.toLowerCase().includes(searchQuery.toLowerCase());

      const types = g.type || [];
      const matchType =
        !selectedType ||
        (selectedType === "none" && types.length === 0) ||
        (selectedType !== "none" && types.includes(selectedType));

      const matchYard = matchesYardSelection(g.yard_ids, selectedYards);

      return matchSearch && matchType && matchYard;
    });
  }, [groups, searchQuery, selectedType, selectedYards]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleSubmit = async (data: {
    types: string[];
    razonSocial: string;
    rfc: string;
    yard_ids: string[];
  }) => {
    if (!assignGroup) return;
    setIsSubmitting(true);
    try {
      const result = await updateGroup(assignGroup.id, {
        name: assignGroup.name,
        type: data.types,
        razonSocial: data.razonSocial,
        rfc: data.rfc,
        yard_ids: data.yard_ids,
      });
      if (!result.success)
        throw new Error(result.error || "updateGroup failed");
      Swal.fire({
        icon: "success",
        title: "Tipo guardado",
        text: "El tipo del grupo se ha guardado correctamente",
        confirmButtonColor: brand.primary,
      });
      setAssignGroup(null);
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un error al guardar el tipo del grupo",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeFilterOptions = useMemo(
    () => [
      { value: "all", label: "Todos los tipos" },
      { value: "none", label: "Sin tipo asignado" },
      ...WATER_TYPES.map((t) => ({ value: t.value, label: t.label })),
    ],
    [],
  );

  // Sin entrada "Todos": en un multi, "todos" es no elegir nada.
  const yardFilterOptions = useMemo(
    () => [
      ...yards.map((y) => ({ value: y.id, label: y.name })),
      { value: NO_YARD_FILTER, label: "Sin patio asignado" },
    ],
    [yards],
  );

  return (
    <div className="h-full flex flex-col relative">
      <HeaderComponent
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        title="Grupos"
        subtitle="Asigna tipos de agua a los grupos. Los vehículos los heredarán."
        searchQuery={searchQuery}
        selectedStatus={selectedType}
        selectedView={selectedView}
        onSearchChange={(v: string) => {
          setSearchQuery(v);
          setCurrentPage(1);
        }}
        onStatusChange={(v: string) => {
          setSelectedType(v);
          setCurrentPage(1);
        }}
        onViewChange={setSelectedView}
        showAddButton={false}
        statusOptions={typeFilterOptions}
        searchPlaceholder="Buscar grupo..."
        extraFilters={[
          {
            multi: true,
            values: selectedYards,
            onChange: (v: string[]) => {
              setSelectedYards(v);
              setCurrentPage(1);
            },
            options: yardFilterOptions,
            placeholder: "Todos los patios",
            noOptionsMessage: "Sin patios disponibles",
          },
        ]}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading ? (
          <div
            className={`py-16 text-center text-sm ${themeClasses.textSecondary}`}
          >
            Cargando grupos...
          </div>
        ) : paginated.length === 0 ? (
          <EmptyState themeClasses={themeClasses} isDarkMode={isDarkMode} />
        ) : selectedView === "list" ? (
          <div className="space-y-3">
            {paginated.map((g) => (
              <GroupListRow
                key={g.id}
                group={g}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                onAssign={setAssignGroup}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginated.map((g) => (
              <GroupGridCard
                key={g.id}
                group={g}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                onAssign={setAssignGroup}
              />
            ))}
          </div>
        )}

        <GroupsPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {assignGroup && (
        <GroupTypeAssignModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          group={assignGroup}
          yards={yards}
          isSubmitting={isSubmitting}
          onClose={() => setAssignGroup(null)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
